import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Check, X, RefreshCw, ShieldCheck, User as UserIcon } from 'lucide-react';
import { Admin, type AdminUser, type PbxExtension } from '@/lib/api';
import clsx from 'clsx';

export default function AdminUsers() {
  const qc = useQueryClient();
  const [modal, setModal] = useState<{ mode: 'create' } | { mode: 'edit'; user: AdminUser } | null>(null);

  const users = useQuery({ queryKey: ['admin-users'], queryFn: Admin.listUsers });
  const removeMut = useMutation({
    mutationFn: (id: number) => Admin.removeUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  return (
    <div className="p-5 max-w-6xl mx-auto">
      <header className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-xl font-semibold">Kullanıcılar</h1>
          <p className="text-sm text-slate-500">ccark agent ve admin'lerini buradan yönet. SIP credentials 3CX'ten canlı çekilir.</p>
        </div>
        <button
          onClick={() => setModal({ mode: 'create' })}
          className="flex items-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
        >
          <Plus size={16} /> Yeni Kullanıcı
        </button>
      </header>

      <div className="bg-surface rounded-lg border border-slate-200 overflow-hidden">
        {users.isLoading && <div className="p-6 text-slate-500">Yükleniyor…</div>}
        {users.error && (
          <div className="p-6 text-red-600">
            Hata: {(users.error as any).message}
          </div>
        )}
        {users.data && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Kullanıcı</th>
                <th className="text-left px-4 py-2">Display</th>
                <th className="text-left px-4 py-2">Extension</th>
                <th className="text-left px-4 py-2">Rol</th>
                <th className="text-left px-4 py-2">Aktif</th>
                <th className="text-right px-4 py-2">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {users.data.map((u) => (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono">{u.username}</td>
                  <td className="px-4 py-2">{u.display_name}</td>
                  <td className="px-4 py-2 font-mono">{u.extension}</td>
                  <td className="px-4 py-2">
                    <span className={clsx(
                      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs',
                      u.role === 'admin' ? 'bg-amber-100 text-amber-700'
                        : u.role === 'leader' ? 'bg-brand-100 text-brand-700'
                        : 'bg-slate-100 text-slate-700',
                    )}>
                      {u.role === 'admin' ? <ShieldCheck size={12} /> : <UserIcon size={12} />}
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {u.is_active ? (
                      <span className="text-emerald-600"><Check size={16} /></span>
                    ) : (
                      <span className="text-red-600"><X size={16} /></span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => setModal({ mode: 'edit', user: u })}
                      className="text-slate-500 hover:text-blue-600 p-1"
                      title="Düzenle"
                    ><Pencil size={16} /></button>
                    <button
                      onClick={() => {
                        if (confirm(`'${u.username}' silinsin mi?`)) removeMut.mutate(u.id);
                      }}
                      className="text-slate-500 hover:text-red-600 p-1 ml-1"
                      title="Sil"
                    ><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
              {users.data.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Henüz kullanıcı yok.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {modal && <UserModal mode={modal.mode} user={modal.mode === 'edit' ? modal.user : undefined} onClose={() => setModal(null)} />}
    </div>
  );
}

interface ModalProps {
  mode: 'create' | 'edit';
  user?: AdminUser;
  onClose: () => void;
}

function UserModal({ mode, user, onClose }: ModalProps) {
  const qc = useQueryClient();
  const [extensions, setExtensions] = useState<PbxExtension[] | null>(null);
  const [filter, setFilter] = useState('');
  const [loadingExt, setLoadingExt] = useState(false);
  const [extError, setExtError] = useState<string | null>(null);

  const [form, setForm] = useState({
    username: user?.username ?? '',
    password: '',
    display_name: user?.display_name ?? '',
    extension: user?.extension ?? '',
    role: user?.role ?? 'agent' as 'admin' | 'leader' | 'agent',
    is_active: user?.is_active ?? true,
    auto_answer: user?.auto_answer ?? false,
  });
  const [err, setErr] = useState<string | null>(null);

  const loadExt = async () => {
    setLoadingExt(true);
    setExtError(null);
    try {
      const list = await Admin.listPbxExtensions();
      setExtensions(list);
    } catch (e: any) {
      setExtError(e.response?.data?.message ?? e.message);
    } finally {
      setLoadingExt(false);
    }
  };

  useEffect(() => { loadExt(); }, []);

  const filtered = useMemo(() => {
    if (!extensions) return [];
    if (!filter.trim()) return extensions;
    const q = filter.toLowerCase();
    return extensions.filter((e) =>
      e.number.includes(q) || e.display_name.toLowerCase().includes(q),
    );
  }, [extensions, filter]);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (mode === 'create') {
        return Admin.createUser(form);
      }
      const patch: any = { ...form };
      if (!form.password) delete patch.password;
      return Admin.updateUser(user!.id, patch);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    },
    onError: (e: any) => {
      setErr(e.response?.data?.message ?? e.message);
    },
  });

  const onExtensionPick = (ext: PbxExtension) => {
    setForm((f) => ({
      ...f,
      extension: ext.number,
      display_name: f.display_name || ext.display_name,
      username: f.username || ext.email?.split('@')[0] || ext.number,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-surface rounded-lg shadow-xl w-[700px] max-h-[90vh] flex flex-col">
        <header className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {mode === 'create' ? 'Yeni Kullanıcı' : `Düzenle: ${user?.username}`}
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700"><X size={20} /></button>
        </header>

        <div className="p-5 overflow-y-auto grid grid-cols-2 gap-4">
          {/* LEFT: 3CX extension picker */}
          <section className="border-r pr-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">3CX Extension Seç</label>
              <div className="flex items-center gap-2">
                {extensions && (
                  <span className="text-xs text-slate-500 font-mono">
                    {filtered.length} / {extensions.length}
                  </span>
                )}
                <button onClick={loadExt} className="text-slate-500 hover:text-slate-700" title="Yenile">
                  <RefreshCw size={14} className={loadingExt ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Numara veya isim ara…"
              className="w-full mb-2 px-2 py-1 border rounded text-sm"
            />
            {extError && <div className="text-xs text-red-600 mb-2">{extError}</div>}
            <div className="border rounded h-96 overflow-y-auto">
              {loadingExt && <div className="p-3 text-sm text-slate-500">Yükleniyor…</div>}
              {filtered.map((ext) => (
                <button
                  key={ext.id}
                  type="button"
                  onClick={() => onExtensionPick(ext)}
                  className={clsx(
                    'w-full text-left px-3 py-2 border-b last:border-b-0 hover:bg-blue-50',
                    form.extension === ext.number && 'bg-blue-100',
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm">{ext.number}</span>
                    <div className="flex items-center gap-1">
                      {!ext.enabled && <span className="text-xs px-1.5 py-0.5 bg-slate-200 text-slate-600 rounded">disabled</span>}
                      {ext.is_registered && <span className="w-2 h-2 bg-emerald-500 rounded-full" title="registered" />}
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 truncate">{ext.display_name || '—'}</div>
                </button>
              ))}
              {!loadingExt && filtered.length === 0 && (
                <div className="p-3 text-sm text-slate-400">Sonuç yok.</div>
              )}
            </div>
          </section>

          {/* RIGHT: form fields */}
          <section className="space-y-3">
            <Field label="Login Kullanıcı Adı">
              <input
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                className="w-full px-2 py-1.5 border rounded text-sm"
                placeholder="örn: dilaver"
              />
            </Field>
            <Field label={`Login Şifre ${mode === 'edit' ? '(boş bırakırsan değişmez)' : ''}`}>
              <div className="flex gap-1">
                <input
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="flex-1 px-2 py-1.5 border rounded text-sm font-mono"
                  placeholder={mode === 'create' ? 'En az 6 karakter' : '— değişmez —'}
                />
                <button
                  type="button"
                  onClick={() => setForm({ ...form, password: genPassword() })}
                  className="px-2 py-1.5 bg-slate-100 hover:bg-slate-200 rounded text-xs"
                >Üret</button>
              </div>
            </Field>
            <Field label="Display Name">
              <input
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                className="w-full px-2 py-1.5 border rounded text-sm"
              />
            </Field>
            <Field label="Extension (3CX)">
              <input
                value={form.extension}
                readOnly
                className="w-full px-2 py-1.5 border rounded text-sm font-mono bg-slate-50"
                placeholder="Soldaki listeden seç"
              />
            </Field>
            <Field label="Rol">
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as any })}
                className="w-full px-2 py-1.5 border rounded text-sm"
              >
                <option value="agent">agent</option>
                <option value="leader">leader</option>
                <option value="admin">admin</option>
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              Aktif (login yapabilir)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.auto_answer}
                onChange={(e) => setForm({ ...form, auto_answer: e.target.checked })}
              />
              Auto-answer (gelen çağrı otomatik açılır)
            </label>
            {err && <div className="text-sm text-red-600">{err}</div>}
          </section>
        </div>

        <footer className="px-5 py-3 border-t flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm hover:bg-slate-100 rounded">İptal</button>
          <button
            onClick={() => saveMut.mutate()}
            disabled={!form.username || !form.extension || (mode === 'create' && !form.password) || saveMut.isPending}
            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-sm rounded"
          >
            {saveMut.isPending ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function genPassword(): string {
  const charset = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
  let out = '';
  const arr = new Uint32Array(14);
  crypto.getRandomValues(arr);
  arr.forEach((n) => { out += charset[n % charset.length]; });
  return out;
}
